// The Signal on the map, the pure part (no Pixi, tested): which relays just lit or just started, how far each of my stars
// is from the capital along my network (the order in which the network's heartbeat reaches them), where the Silence's
// front stands in the season, and how much of all this the machine can afford (the « Très bas » preset).
import type { RelayView } from '@aurane/sim';

/** full: every effect; lite (« Très bas », iGPU): the one-shot effects only, shorter, no particles, no heartbeat, a still
 *  Silence; off (reduced motion): nothing moves, the map is as before. */
export type FxLevel = 'full' | 'lite' | 'off';

export function fxLevelOf(quality: string | null | undefined, reducedMotion: boolean): FxLevel {
  if (reducedMotion || quality === 'off') return 'off';
  return quality === 'verylow' || quality === 'low' ? 'lite' : 'full';
}

/** The level for this page: the donjon's quality choice (the same « Très bas » switch), the system's reduced motion. */
export function fxLevel(): FxLevel {
  let q: string | null = null;
  try { q = new URLSearchParams(location.search).get('fx') ?? localStorage.getItem('aurane.donjon.quality'); } catch { /* private window */ }
  let reduced = false;
  try { reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { /* no matchMedia */ }
  return fxLevelOf(q, reduced);
}

export interface RelayChange { id: string; a: string; b: string }

/** My relays that lit up between two views (ready now, not ready or absent before), and those that started (new and not
 *  ready). The first view has no « before »: nothing is new, what is already lit is not replayed. */
export function relayChanges(prev: readonly RelayView[] | null, next: readonly RelayView[], me: string): { lit: RelayChange[]; started: RelayChange[] } {
  if (!prev) return { lit: [], started: [] };
  const before = new Map(prev.map((r) => [r.id, r]));
  const lit: RelayChange[] = [], started: RelayChange[] = [];
  for (const r of next) {
    if (r.owner !== me || r.cut) continue;
    const old = before.get(r.id);
    if (r.ready && (!old || !old.ready || old.cut)) lit.push({ id: r.id, a: r.a, b: r.b });
    else if (!r.ready && !old) started.push({ id: r.id, a: r.a, b: r.b });
  }
  return { lit, started };
}

/** Hops from the capital along my lit relays (breadth first); a star not reached is absent. */
export function networkDepth(relays: readonly RelayView[], capital: string, me: string): Map<string, number> {
  const adj = new Map<string, string[]>();
  for (const r of relays) {
    if (r.owner !== me || !r.ready || r.cut) continue;
    (adj.get(r.a) ?? adj.set(r.a, []).get(r.a)!).push(r.b);
    (adj.get(r.b) ?? adj.set(r.b, []).get(r.b)!).push(r.a);
  }
  const depth = new Map<string, number>([[capital, 0]]);
  const queue = [capital];
  while (queue.length) {
    const s = queue.shift()!;
    for (const n of adj.get(s) ?? []) if (!depth.has(n)) { depth.set(n, depth.get(s)! + 1); queue.push(n); }
  }
  return depth;
}

/** The end a lit relay's Signal comes from: the one nearer the capital (else `a`). */
export function sourceEnd(r: RelayChange, depth: ReadonlyMap<string, number>): { from: string; to: string } {
  const da = depth.get(r.a) ?? Infinity, db = depth.get(r.b) ?? Infinity;
  return db < da ? { from: r.b, to: r.a } : { from: r.a, to: r.b };
}

/** How far the season has gone, 0 at its start, 1 when the Silence falls. */
export function seasonProgress(time: number, endsAt: number, seasonDays: number): number {
  const span = Math.max(1, seasonDays * 86400);
  return Math.min(1, Math.max(0, 1 - (endsAt - time) / span));
}

/** The radius of the Silence's front (world units) for a galaxy of radius `galaxyPx`: beyond the rim at the start of the
 *  season, a third of the galaxy left when it falls. The front moves faster at the end (the dread of the last weeks). */
export function silenceFront(progress: number, galaxyPx: number): number {
  const p = Math.min(1, Math.max(0, progress));
  return galaxyPx * (1.22 - 0.9 * p ** 1.6);
}
